/**
 * Audio and haptic feedback for the sweep.
 *
 * You are looking at the PACKET, not at the screen, so the loop has to be
 * usable with your eyes elsewhere. Every note sits in the 1-2kHz band because
 * phone speakers roll off bass hard — the first version used 520Hz at low gain
 * for 50ms and was reported, correctly, as "no beep nothing happened".
 *
 * Vibration is the fallback, because the phone is usually on silent.
 */

type Cue = "name" | "date" | "done" | "switch" | "gate";

let ac: AudioContext | null = null;

/** Must be called from a user gesture — browsers block audio otherwise. */
export function enableSound(): void {
  if (ac) {
    if (ac.state === "suspended") void ac.resume();
    return;
  }
  const Ctor =
    window.AudioContext ??
    (window as unknown as { webkitAudioContext?: typeof AudioContext })
      .webkitAudioContext;
  if (Ctor) ac = new Ctor();
}

function tone(freqs: number[], dur = 0.15, gain = 0.5): void {
  if (!ac || ac.state === "closed") return;
  if (ac.state === "suspended") void ac.resume();
  freqs.forEach((f, i) => {
    if (!ac) return;
    const o = ac.createOscillator();
    const g = ac.createGain();
    const t0 = ac.currentTime + i * dur * 0.8;
    // Triangle rather than sine: the extra harmonics carry on a tiny speaker.
    o.type = "triangle";
    o.frequency.setValueAtTime(f, t0);
    g.gain.setValueAtTime(0, t0);
    g.gain.linearRampToValueAtTime(gain, t0 + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
    o.connect(g).connect(ac.destination);
    o.start(t0);
    o.stop(t0 + dur + 0.02);
  });
}

function buzz(pattern: number | number[]): void {
  try {
    navigator.vibrate?.(pattern);
  } catch {
    /* unsupported — the tone is the primary cue anyway */
  }
}

const CUES: Record<Cue, () => void> = {
  name: () => {
    tone([1320]);
    buzz(30);
  },
  date: () => {
    tone([1320, 1760]);
    buzz([30, 60, 30]);
  },
  done: () => {
    tone([1046, 1320, 1976], 0.13);
    buzz(120);
  },
  switch: () => {
    tone([880, 660], 0.1);
    buzz([60, 40, 60]);
  },
  gate: () => {
    tone([1500], 0.06, 0.3);
    buzz(15);
  },
};

export function beep(cue: Cue): void {
  CUES[cue]();
}
